import { useState } from "react"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { Label } from "@/components/ui/label"
import { Loader2, FileText } from "lucide-react"
import { useToast } from "@/components/ui/use-toast"
import { useSaveMessageAsReport } from "@/services/query/reports.query"

export function AddReportFromChatDialog({
  open,
  onOpenChange,
  messageId,
  patientId,
  fileName,
}) {
  const [title, setTitle] = useState("")
  const [details, setDetails] = useState("")
  const { toast } = useToast()

  const { mutate: saveAsReport, isLoading } = useSaveMessageAsReport()

  const handleSubmit = (e) => {
    e.preventDefault()

    if (!title.trim()) {
      toast({
        title: "Error",
        description: "Please enter a report title",
        variant: "destructive",
      })
      return
    }

    saveAsReport(
      {
        patientId,
        data: {
          title: title.trim(),
          details: details.trim(),
          message_id: messageId,
        },
      },
      {
        onSuccess: () => {
          toast({
            title: "Success",
            description: "File saved as report successfully",
          })
          onOpenChange(false)
          setTitle("")
          setDetails("")
        },
        onError: (error) => {
          console.error("Save as report error:", error)
          toast({
            title: "Error",
            description:
              error?.response?.data?.message || "Failed to save file as report",
            variant: "destructive",
          })
        },
      }
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-blue-500" />
            Save as Report
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="text-sm text-gray-500 pb-2 border-b">
            File: {fileName}
          </div>

          <div className="space-y-2">
            <Label htmlFor="title">
              Report Title <span className="text-red-500">*</span>
            </Label>
            <Input
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Enter report title"
              maxLength={100}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="details">Description</Label>
            <Textarea
              id="details"
              value={details}
              onChange={(e) => setDetails(e.target.value)}
              placeholder="Enter report description (optional)"
              className="resize-none"
              rows={3}
              maxLength={500}
            />
          </div>

          <DialogFooter className="sm:justify-start">
            <Button
              type="submit"
              disabled={isLoading || !title.trim()}
              className="w-full sm:w-auto"
            >
              {isLoading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                "Save as Report"
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
